<script setup lang="ts">
import type { LucideIcon } from '@lucide/vue'
import { ClipboardList, Inbox, MessageCircle, ShieldCheck, Sparkles, UserRound, UsersRound } from '@lucide/vue'
import { ZhStatusPill, ZhTooltip } from '@zerohuman/ui'
import { computed } from 'vue'
import { CHIP_PAGES, type EnterpriseChip, type EnterpriseChipKey } from '@/lib/enterprisePicker'

/**
 * An enterprise's boxes on the picker, on a card or in a table row. A box counting what waits on the person is a
 * button of its own: it opens that enterprise on the page where the work waits (unread chats on Chat, gates on
 * Gates), so the person lands on it rather than on the map. Its size, in members and teams, opens nothing.
 */
const props = defineProps<{ chips: EnterpriseChip[]; enterpriseName: string; disabled?: boolean }>()
const emit = defineEmits<{ open: [path: string] }>()

const CHIP_ICONS: Record<EnterpriseChipKey, LucideIcon> = {
    members: UserRound,
    teams: UsersRound,
    chats: MessageCircle,
    mail: Inbox,
    gates: ShieldCheck,
    plans: ClipboardList,
    recommendations: Sparkles,
}

const boxes = computed(() =>
    props.chips.map(chip => {
        const page = CHIP_PAGES[chip.key]
        return {
            chip,
            page,
            hint: page ? `${chip.label}. Open ${page.name}` : chip.label,
            name: page ? `${chip.label}: open ${page.name} in ${props.enterpriseName}` : undefined,
        }
    }),
)
</script>

<template>
	<ZhTooltip v-for="box in boxes" :key="box.chip.key" :text="box.hint">
		<!-- `.stop`: the card or row around it opens the enterprise on the map, and this opens it on the page. -->
		<button
			v-if="box.page"
			type="button"
			class="inline-flex cursor-pointer hover:opacity-80 disabled:cursor-default disabled:opacity-100"
			:aria-label="box.name"
			:disabled="disabled"
			:data-chip-page="box.page.path"
			@click.stop="emit('open', box.page.path)"
		>
			<ZhStatusPill :tone="box.chip.tone" :count="box.chip.count" :pulse="false" :data-chip="box.chip.key" :aria-label="box.chip.label">
				<component :is="CHIP_ICONS[box.chip.key]" class="size-3" aria-hidden="true" />{{ box.chip.count }}
			</ZhStatusPill>
		</button>
		<ZhStatusPill v-else :tone="box.chip.tone" :count="box.chip.count" :pulse="false" :data-chip="box.chip.key" :aria-label="box.chip.label">
			<component :is="CHIP_ICONS[box.chip.key]" class="size-3" aria-hidden="true" />{{ box.chip.count }}
		</ZhStatusPill>
	</ZhTooltip>
</template>
